import { Component, OnInit, OnDestroy, Input, Optional, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatList, MatListItem } from '@angular/material/list';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ClinicianDetailsActions, ProviderUploadResults } from '@store/actions/clinician-details/clinician-details.actions';
import { 
  selectProviderUploadLoading,
  selectProviderUploadProgress,
  selectProviderUploadError,
  selectProviderUploadComplete,
  selectProviderUploadResults
} from '@store/selectors/clinician-details/clinician-details.selectors';
import { selectOrgId } from '@store/selectors/organization-emr-details/organization-emr-details.selector';
import { MAX_FILE_SIZE } from '@constants/file-upload';
import { downloadBlob } from '../../utils/blob-viewer.util';

@Component({
  selector: 'app-provider-upload',
  templateUrl: './provider-upload.component.html',
  styleUrls: ['./provider-upload.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatList,
    MatListItem
  ],
})
export class ProviderUploadComponent implements OnInit, OnDestroy {

  @Input() organizationId?: string;
  
  private destroy$ = new Subject<void>();
  
  uploadLoading$: Observable<boolean>;
  uploadProgress$: Observable<number>;
  uploadError$: Observable<string | null>;
  uploadComplete$: Observable<boolean>;
  uploadResults$: Observable<ProviderUploadResults | null>;
  organizationId$: Observable<string | undefined>;

  selectedFile: File | null = null;
  isDragOver = false;
  uploadStarted = false;
  showAllErrors = false;

  constructor(
    private store: Store,
    @Optional() private dialogRef: MatDialogRef<ProviderUploadComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.uploadLoading$ = this.store.select(selectProviderUploadLoading);
    this.uploadProgress$ = this.store.select(selectProviderUploadProgress);
    this.uploadError$ = this.store.select(selectProviderUploadError);
    this.uploadComplete$ = this.store.select(selectProviderUploadComplete);
    this.uploadResults$ = this.store.select(selectProviderUploadResults);
    this.organizationId$ = this.store.select(selectOrgId);
  }

  ngOnInit(): void {
    this.store.dispatch(ClinicianDetailsActions.clearProviderUploadState());
    
    this.uploadComplete$.pipe(takeUntil(this.destroy$)).subscribe(complete => {
      if (complete) {
        this.uploadStarted = false;
      }
    });

    this.uploadError$.pipe(takeUntil(this.destroy$)).subscribe(error => {
      if (error) {
        this.uploadStarted = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  downloadTemplate(): void {
    const headers = ['FirstName', 'LastName', 'NPI', 'Email', 'Phone', 'SpecialtyCode'];
    const sampleData = ['John', 'Doe', '1234567890', 'john.doe@example.com', '1112223333', '235Z00000X'];
    
    const csvContent = [
      headers.join(','),
      sampleData.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    downloadBlob(blob, 'provider-upload-template.csv');
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFileSelection(input.files[0]);
    }
  }

  private handleFileSelection(file: File): void {
    const allowedExtensions = ['.xlsx', '.csv'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedExtensions.includes(fileExtension)) {
      alert('Please select a valid Excel (.xlsx) or CSV (.csv) file');
      return;
    }

    const maxSize = MAX_FILE_SIZE;
    if (file.size > maxSize) {
      alert('File size cannot exceed 25MB');
      return;
    }

    this.selectedFile = file;
  }

  removeFile(): void {
    this.selectedFile = null;
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  uploadFile(): void {
    if (!this.selectedFile) {
      return;
    }
    this.uploadStarted = true;
    
    if (this.organizationId) {
      this.store.dispatch(ClinicianDetailsActions.uploadProviders({
        file: this.selectedFile,
        organizationId: this.organizationId
      }));
    } else {
      this.organizationId$.pipe(takeUntil(this.destroy$)).subscribe(orgId => {
        if (orgId && this.selectedFile) {
          this.store.dispatch(ClinicianDetailsActions.uploadProviders({
            file: this.selectedFile,
            organizationId: orgId
          }));
        } else {
          console.error('Organization ID not found');
        }
      });
    }
  }

  uploadAnother(): void {
    this.selectedFile = null;
    this.uploadStarted = false; 
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    this.store.dispatch(ClinicianDetailsActions.clearProviderUploadState());
  }

  clearError(): void {
    this.uploadStarted = false;
    this.selectedFile = null; 

    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    this.store.dispatch(ClinicianDetailsActions.clearProviderUploadState());
  }

  closeModal(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getMissingInfoErrors(errors: any[]): any[] {
    return errors.filter(error => 
      error.message.toLowerCase().includes('missing')
    );
  }

  getInvalidEmailErrors(errors: any[]): any[] {
    return errors.filter(error => 
      error.message.toLowerCase().includes('invalid email')
    );
  }

  getDuplicateNPIErrors(errors: any[]): any[] {
    return errors.filter(error => 
      error.message.toLowerCase().includes('duplicate') || 
      error.message.toLowerCase().includes('already exists')
    );
  }

  getOtherErrors(errors: any[]): any[] {
    return errors.filter(error => 
      !this.getMissingInfoErrors([error]).length && 
      !this.getInvalidEmailErrors([error]).length &&
      !this.getDuplicateNPIErrors([error]).length
    );
  }

  toggleErrors(): void {
    this.showAllErrors = !this.showAllErrors;
  }
}